import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ADMIN_EMAIL } from "@/api/admin";
import { ADMIN_BASE_PATH } from "@/components/admin/adminConstants";
import { useNoIndex } from "@/lib/noIndex";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/TextField";
import { useAuth } from "@/contexts/AuthContext";

export function AdminLoginPage() {
  const { session, signIn, configured } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useNoIndex();

  useEffect(() => {
    document.title = "Daywia";
  }, []);

  if (session?.user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
    return <Navigate to={ADMIN_BASE_PATH} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const normalized = ADMIN_EMAIL.toLowerCase();
    setBusy(true);
    try {
      await signIn(normalized, password);
      navigate(ADMIN_BASE_PATH, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-panel flex min-h-dvh flex-col items-center justify-center bg-[#f3f4f6] px-4 py-8">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary">Daywia Admin</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Platform controls for ads, users, posts, and promotions.
          </p>
        </div>

        {!configured ? (
          <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
            Add Supabase env vars in <strong>web/.env</strong>.
          </p>
        ) : null}

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-2xl border border-black/[0.06] bg-white p-6 shadow-sm"
        >
          <div className="space-y-1">
            <p className="text-sm font-medium text-ink">Admin account</p>
            <p className="rounded-xl border border-black/[0.08] bg-[#f8fafc] px-3 py-2.5 text-sm text-ink-muted">
              {ADMIN_EMAIL}
            </p>
            <p className="text-xs text-ink-muted">
              Sign in with the password registered for this account in Supabase Auth.
            </p>
          </div>
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" fullWidth disabled={busy || !configured}>
            {busy ? "Signing in…" : "Sign in to admin"}
          </Button>
        </form>
      </div>
    </div>
  );
}
